import * as React from 'react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import PlaylistAddCheckOutlinedIcon from '@mui/icons-material/PlaylistAddCheckOutlined';

import useAxios from '../utils/useAxios';
import PageShell from './layout/PageShell';
import { EmptyState, NumberField, SectionCard, number } from './ui';
import { errorText } from './OrderLines';
import { TRUE_COLORS } from '../theme';

/** A barcode a cashier sold before it was in the catalog (Receipt-Web-Server api/pending_products.py). */
export type PendingProduct = {
  id: number; barcode: string; name: string; price: number; times_sold: number; first_sold_at: string; last_sold_at: string; last_seller: string;
};

/**
 * /admin/products/pending: "Por registrar". Cashiers can't add products, so a barcode the catalog doesn't have is sold
 * as a one-off line with a name and a price; here an administrator adds it to the catalog (every register then finds
 * it) or dismisses it.
 */
const PendingProducts = () => {
  const axios = useAxios();
  const navigate = useNavigate();
  const [pending, setPending] = React.useState<PendingProduct[] | null>(null);
  const [drafts, setDrafts] = React.useState<Record<number, { name: string; price: number | null }>>({});
  const [busy, setBusy] = React.useState<number | null>(null);
  const [error, setError] = React.useState('');
  const [notice, setNotice] = React.useState('');

  const load = React.useCallback(() => {
    axios.get('api/pending-products/').then(r => setPending(r.data ?? [])).catch(() => setPending([]));
  }, [axios]);
  React.useEffect(load, [load]);

  const draftOf = (p: PendingProduct) => drafts[p.id] ?? { name: p.name, price: p.price };
  const act = async (p: PendingProduct, add: boolean) => {
    setBusy(p.id);
    setError('');
    try {
      if (add) {
        const draft = draftOf(p);
        await axios.post(`api/pending-products/${p.id}/add/`, { name: draft.name, price: draft.price ?? 0 });
        setNotice(`«${draft.name}» ya está en tu catálogo; tus cajas lo reciben en un momento.`);
      } else {
        await axios.delete(`api/pending-products/${p.id}/`);
      }
      load();
    } catch (err) {
      setError(errorText(err, 'No se pudo guardar.'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <PageShell title="Por registrar" subtitle="Productos que tus cajeros vendieron sin estar en tu catálogo: agrégalos con un clic o descártalos."
      actions={<Button color="inherit" onClick={() => navigate('/admin/products')}>Ver productos</Button>}>
      <Stack spacing={2}>
        {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
        {notice && <Alert severity="success" onClose={() => setNotice('')}>{notice}</Alert>}
        <SectionCard noPadding>
          {pending && pending.length === 0 ? (
            <EmptyState icon={<PlaylistAddCheckOutlinedIcon />} title="Nada por registrar"
              description="Cuando un cajero venda un código que no está en tu catálogo, aparece aquí." />
          ) : (
            <Stack divider={<Box sx={{ borderTop: `1px solid ${TRUE_COLORS.borderSolid}` }} />}>
              {(pending ?? []).map(p => {
                const draft = draftOf(p);
                return (
                  <Stack key={p.id} direction={{ xs: 'column', md: 'row' }} spacing={1.5} data-testid="pending-product"
                    sx={{ px: 2.5, py: 2, alignItems: { md: 'center' } }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <TextField size="small" fullWidth label="Nombre" value={draft.name}
                        onChange={e => setDrafts(d => ({ ...d, [p.id]: { ...draft, name: e.target.value } }))} />
                      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.75 }}>
                        {p.barcode} · vendido {number(p.times_sold)} {p.times_sold === 1 ? 'vez' : 'veces'} · último {dayjs(p.last_sold_at).fromNow()}
                        {p.last_seller ? ` por ${p.last_seller}` : ''}
                      </Typography>
                    </Box>
                    <NumberField size="small" label="Precio" value={draft.price} sx={{ width: { md: 130 } }}
                      onChange={v => setDrafts(d => ({ ...d, [p.id]: { ...draft, price: v } }))} />
                    <Stack direction="row" spacing={1}>
                      <Button variant="contained" disabled={busy === p.id || !draft.name.trim() || draft.price == null} onClick={() => act(p, true)}
                        data-testid="pending-add">Agregar al catálogo</Button>
                      <Button color="inherit" disabled={busy === p.id} onClick={() => act(p, false)}>Descartar</Button>
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>
          )}
        </SectionCard>
      </Stack>
    </PageShell>
  );
};

export default PendingProducts;
